import { ComponentType, lazy, LazyExoticComponent } from 'react';

/**
 * Resilient wrapper around `React.lazy`: retries a failed chunk import once,
 * then does a single guarded page reload to pick up rotated chunk hashes
 * before rethrowing, instead of looping or blanking the page.
 */

const RETRY_DELAY_MS = 500;
const RELOAD_FLAG_PREFIX = 'lazyRetryReloaded:';

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const getSessionStorage = (): Storage | null => {
    try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
            return window.sessionStorage;
        }
    } catch {}
    return null;
};

const hasReloadedThisSession = (flagKey: string): boolean => {
    try {
        return getSessionStorage()?.getItem(flagKey) === '1';
    } catch {
        return false;
    }
};

const markReloadedThisSession = (flagKey: string): boolean => {
    const storage = getSessionStorage();
    if (!storage) return false;
    try {
        storage.setItem(flagKey, '1');
        return true;
    } catch {
        return false;
    }
};

const clearReloadFlag = (flagKey: string): void => {
    try {
        getSessionStorage()?.removeItem(flagKey);
    } catch {}
};

const reloadPage = (): boolean => {
    try {
        if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
            window.location.reload();
            return true;
        }
    } catch {}
    return false;
};

/**
 * Runs a dynamic-import factory with the retry-then-reload recovery described
 * above. Exported (separately from `lazyWithRetry`) so the recovery logic can be
 * unit-tested directly without rendering a Suspense tree.
 */
export const retryDynamicImport = async <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
    chunkName: string,
    retryDelayMs: number = RETRY_DELAY_MS,
): Promise<{ default: T }> => {
    const flagKey = `${RELOAD_FLAG_PREFIX}${chunkName}`;

    try {
        const module = await factory();
        clearReloadFlag(flagKey);
        return module;
    } catch {
        // First attempt failed — wait briefly and try exactly once more.
        await delay(retryDelayMs);

        try {
            const module = await factory();
            clearReloadFlag(flagKey);
            return module;
        } catch (retryError) {
            // Only suspend indefinitely if we could both record the reload flag
            // and actually kick off the reload — otherwise there's no browser
            // reload in flight to wait on, so fall through and surface the error.
            if (!hasReloadedThisSession(flagKey) && markReloadedThisSession(flagKey) && reloadPage()) {
                // Keep Suspense showing its fallback while the browser reloads;
                // this promise intentionally never settles.
                return new Promise<{ default: T }>(() => undefined);
            }

            // Already reloaded once (or couldn't reload) and it's still failing —
            // surface the error (an error boundary / Suspense can handle it)
            // instead of looping.
            clearReloadFlag(flagKey);
            throw retryError;
        }
    }
};

/**
 * Drop-in replacement for `React.lazy` that adds chunk-load resilience.
 * `chunkName` is a stable identifier used to scope the per-session reload flag.
 */
export const lazyWithRetry = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>,
    chunkName: string,
): LazyExoticComponent<T> => lazy(() => retryDynamicImport(factory, chunkName));

export default lazyWithRetry;
